import { ReactElement, useMemo } from "react";
import {
	BodyRowIndexContext,
	ColumnContextData,
	ColumnsContext,
	ColumnsContextData
} from "./contexts";
import { TableViewColumnDefinition } from "./TableView";
import { ColumnState } from "./Column";
import { useValueEqualMemo } from "client/Hooks";
import styles from "./TableView.module.less";
import { setTrackingId } from "client/Misc/utils";

export type BodyProps<TItem> = {
	numberOfSkippedElements: number;
	numberOfVisibleElements: number;
	items: readonly TItem[];
	itemKey: StringKeyOf<TItem> | ((item: TItem, index: number) => number | string);
	columnDefinitions: readonly TableViewColumnDefinition[];
	columnsState: readonly ColumnState[];
	children(item: TItem, itemKey: string | number): ReactElement;
};

export const Body = <TItem,>(props: BodyProps<TItem>): ReactElement => {
	const {
		numberOfSkippedElements,
		numberOfVisibleElements,
		items,
		itemKey,
		columnDefinitions,
		columnsState,
		children
	} = props;

	const resolveItemKey = useMemo(
		(): ((item: TItem, index: number) => number | string) => {
			if (typeof itemKey === "function") {
				return itemKey;
			}

			return (item: TItem, index: number): number | string => {
				const key: unknown = item[itemKey];

				if (typeof key === "string" || typeof key === "number") {
					return key;
				}

				console.error("Key must be either string or number. Falling back to index");

				return `key_5c21d419-ce5f-4d07-a6d0-97cf959a453b_${index}`;
			};
		},
		[itemKey]
	);

	const visibleElements = useMemo(
		(): readonly TItem[] => {
			const end: number = Math.min(
				items.length,
				numberOfSkippedElements + numberOfVisibleElements
			);

			return items.slice(numberOfSkippedElements, end);
		},
		[
			items,
			numberOfSkippedElements,
			numberOfVisibleElements
		]
	);

	const columnsContextData = useMemo(
		(): ColumnsContextData => ({
			columns: columnDefinitions.map((column: TableViewColumnDefinition): ColumnContextData => {
				const columnState: ColumnState | undefined = columnsState.find(c => c.id === column.trackingId);

				return {
					isExpensiveToRender: column.isExpensiveToRender ?? false,
					trackingId: column.trackingId,
					maxWidthOfAutoColumn: columnState?.width === "auto"
						? columnState?.maxWidth
						: undefined
				};
			})
		}),
		[columnDefinitions, columnsState]
	);

	// NOTE: Since all cells in the table are subscribed to this context
	// we make it stable using value comparison, to avoid unnecessary re-renders
	// of entire table during column resize for example.
	const stableColumnContextData: ColumnsContextData = useValueEqualMemo(
		() => columnsContextData,
		[columnsContextData]
	);

	return (
		<div
			{...setTrackingId("body")}
			className={styles.body}
		>
			<ColumnsContext.Provider value={stableColumnContextData}>
				{visibleElements.map((item, index): ReactElement => {
					const itemKey: string | number = resolveItemKey(item, index + numberOfSkippedElements);

					return (
						<BodyRowIndexContext.Provider value={index + numberOfSkippedElements}
							key={itemKey}
						>
							{children(item, itemKey)}
						</BodyRowIndexContext.Provider>
					);
				})}
			</ColumnsContext.Provider>
		</div>
	);
};
Body.displayName = "Body";